import { Module } from '@/types/courseTypes';
import { UnifiedCourseService } from './unifiedCourseService';
import { ModuleCache } from '@/utils/EnhancedModuleCache';
import { logger } from '@/utils/logger';

/**
 * Intelligent Prefetch Service
 * Preloads adjacent modules for instant navigation
 */

class PrefetchServiceManager {
  private prefetchQueue = new Set<string>();
  private isProcessing = false;
  private readonly MAX_QUEUE_SIZE = 5;
  private readonly PREFETCH_DELAY = 100; // ms between prefetch operations

  /**
   * Prefetch next and previous modules relative to current
   */
  async prefetchAdjacent(
    courseId: string,
    currentModuleId: string,
    modules: Module[]
  ): Promise<void> {
    const currentIndex = modules.findIndex(m => m.id === currentModuleId);
    
    if (currentIndex === -1) {
      logger.warn('[PrefetchService] Current module not found in modules list');
      return;
    }

    // High priority: Next module
    if (currentIndex < modules.length - 1) {
      const nextModule = modules[currentIndex + 1];
      this.queuePrefetch(courseId, nextModule.id, 'next');
    }

    // Medium priority: Previous module
    if (currentIndex > 0) {
      const prevModule = modules[currentIndex - 1];
      this.queuePrefetch(courseId, prevModule.id, 'previous');
    }

    // Low priority: Module after next
    if (currentIndex < modules.length - 2) {
      const afterNextModule = modules[currentIndex + 2];
      this.queuePrefetch(courseId, afterNextModule.id, 'after-next', true);
    }

    // Also prefetch audio metadata for adjacent modules
    const { audioPrefetchService } = await import('./audio/AudioPrefetchService');
    audioPrefetchService.prefetchAdjacent(courseId, currentModuleId, modules);
  }

  /**
   * Prefetch specific module by ID
   */
  async prefetchModule(courseId: string, moduleId: string): Promise<void> {
    const key = `${courseId}:${moduleId}`;
    
    // Skip if already cached and fresh
    if (ModuleCache.has(key) && !ModuleCache.isStale(key)) {
      logger.debug(`[PrefetchService] Skip: ${key} (already cached)`);
      return;
    }

    try {
      logger.debug(`[PrefetchService] Prefetching: ${key}`);
      const module = await UnifiedCourseService.getCompleteModule(moduleId, courseId);
      
      if (module) {
        ModuleCache.setComplete(key, module);
        logger.debug(`[PrefetchService] Success: ${key}`);
      }
    } catch (error) {
      logger.warn(`[PrefetchService] Failed: ${key}`, error);
    }
  }

  /**
   * Queue module for prefetch
   */
  private queuePrefetch(
    courseId: string,
    moduleId: string,
    priority: 'next' | 'previous' | 'after-next',
    lowPriority = false
  ): void {
    const key = `${courseId}:${moduleId}`;
    
    // Skip if already queued or cached
    if (this.prefetchQueue.has(key) || (ModuleCache.has(key) && !ModuleCache.isStale(key))) {
      return;
    }

    // Check queue size limit
    if (this.prefetchQueue.size >= this.MAX_QUEUE_SIZE) {
      logger.debug('[PrefetchService] Queue full, skipping prefetch');
      return;
    }

    this.prefetchQueue.add(key);
    logger.debug(`[PrefetchService] Queued [${priority}]: ${key}`);

    // Process queue after short delay (unless low priority)
    if (!lowPriority) {
      setTimeout(() => this.processQueue(), this.PREFETCH_DELAY);
    } else {
      setTimeout(() => this.processQueue(), this.PREFETCH_DELAY * 5);
    }
  }

  /**
   * Process prefetch queue
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.prefetchQueue.size === 0) {
      return;
    }

    this.isProcessing = true;

    try {
      const batch = Array.from(this.prefetchQueue).slice(0, 3); // Process up to 3 at once
      
      await Promise.all(
        batch.map(async (key) => {
          const [courseId, moduleId] = key.split(':');
          await this.prefetchModule(courseId, moduleId);
          this.prefetchQueue.delete(key);
        })
      );
    } catch (error) {
      logger.error('[PrefetchService] Queue processing error:', error);
    } finally {
      this.isProcessing = false;
      
      // Continue processing if queue not empty
      if (this.prefetchQueue.size > 0) {
        setTimeout(() => this.processQueue(), this.PREFETCH_DELAY);
      }
    }
  }

  /**
   * Prefetch entire course modules in background
   */
  async prefetchCourse(courseId: string): Promise<void> {
    try {
      logger.info(`[PrefetchService] Prefetching course: ${courseId}`);
      const modules = await UnifiedCourseService.getCourseModules(courseId);
      
      // Warm cache with all modules
      const warmData = modules.map(module => ({
        key: `${courseId}:${module.id}`,
        module
      }));
      
      await ModuleCache.warmCache(warmData);
      logger.info(`[PrefetchService] Course prefetched: ${courseId} (${modules.length} modules)`);
    } catch (error) {
      logger.error(`[PrefetchService] Course prefetch failed: ${courseId}`, error);
    }
  }

  /**
   * Clear prefetch queue
   */
  clearQueue(): void {
    this.prefetchQueue.clear();
    logger.debug('[PrefetchService] Queue cleared');
  }

  /**
   * Get queue status
   */
  getStatus(): {
    queueSize: number;
    isProcessing: boolean;
    maxQueueSize: number;
  } {
    return {
      queueSize: this.prefetchQueue.size,
      isProcessing: this.isProcessing,
      maxQueueSize: this.MAX_QUEUE_SIZE
    };
  }
}

// Export singleton instance
export const PrefetchService = new PrefetchServiceManager();
